import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

/**
 * Sends fire-and-forget activity events to the backend.
 *
 * We keep this file dumb on purpose:
 *  - Never awaited (page navigation continues even if the log call is slow).
 *  - Never surfaces errors to the user — logging is best-effort.
 *  - Auto-hooked to the router in `ActivityTracker` below so every route
 *    change is captured without touching every page.
 */
export const trackActivity = (event, extras = {}) => {
  try {
    // Fire-and-forget — no await, no throw.
    apiClient
      .post('/activity/track', {
        event,
        resource_type: extras.resource_type || null,
        resource_id: extras.resource_id || null,
        context: extras.context || {},
      })
      .catch(() => {});
  } catch {
    /* swallow — logging must never break the app */
  }
};

/**
 * Mount once at the top of the app (inside AuthProvider + Router).
 * Emits:
 *   • `page_view` on every pathname change (with the full path in context).
 *   • `session_ping` every 60 seconds while the tab is visible.
 *   • `session_end` on tab close.
 */
export const ActivityTracker = () => {
  const location = useLocation();
  const { user } = useAuth();
  const lastPath = useRef(null);

  // Page-view tracking on route change (only when logged in).
  useEffect(() => {
    if (!user) return;
    const path = location.pathname + (location.search || '');
    if (path === lastPath.current) return;
    lastPath.current = path;
    trackActivity('page_view', {
      resource_type: 'route',
      resource_id: location.pathname,
      context: {
        path,
        search: location.search || '',
        referrer: document.referrer || '',
        title: document.title || '',
      },
    });
  }, [location.pathname, location.search, user]);

  // Session heartbeat + close tracking.
  useEffect(() => {
    if (!user) return;

    const pingInterval = setInterval(() => {
      // Only ping while the tab is actually visible — no noise from
      // background tabs sitting idle.
      if (document.visibilityState === 'visible') {
        trackActivity('session_ping', {
          context: { path: window.location.pathname },
        });
      }
    }, 60_000);

    const handleUnload = () => {
      // Best-effort — the browser may kill fetch mid-flight, so we use
      // `navigator.sendBeacon` which is designed exactly for this.
      try {
        const token = localStorage.getItem('elevate360_token');
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/activity/track`;
        // sendBeacon can't set an Authorization header, so the token rides
        // in the body. Never put it in the URL: query strings are logged.
        const body = JSON.stringify({
          event: 'session_end',
          context: { path: window.location.pathname },
          token,
        });
        if (token && navigator.sendBeacon) {
          const blob = new Blob([body], { type: 'application/json' });
          navigator.sendBeacon(url, blob);
        }
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearInterval(pingInterval);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [user]);

  return null;
};

export default ActivityTracker;
